import React, { useState } from 'react';
import { Plus, Eye, Code, BarChart3, Settings, Calendar, Download, Users, FileText, Megaphone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FormField {
  id: string;
  type: 'text' | 'email' | 'phone' | 'select' | 'textarea' | 'checkbox' | 'date';
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
}

interface MarketingForm {
  id: string;
  name: string;
  type: 'lead-generation' | 'event-registration' | 'survey' | 'contact' | 'demo-request' | 'newsletter';
  description: string;
  fields: FormField[];
  submissions: number;
  conversionRate: number;
  status: 'active' | 'draft' | 'paused';
  createdBy: string;
  createdDate: string;
  lastModified: string;
}

const MarketingForms: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'forms' | 'create' | 'submissions'>('forms');
  const [selectedForm, setSelectedForm] = useState<MarketingForm | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Sample marketing forms data
  const [forms] = useState<MarketingForm[]>([
    {
      id: '1',
      name: 'Product Demo Request',
      type: 'demo-request',
      description: 'Capture leads interested in product demonstrations',
      fields: [
        { id: '1', type: 'text', label: 'Full Name', required: true },
        { id: '2', type: 'email', label: 'Business Email', required: true },
        { id: '3', type: 'text', label: 'Company Name', required: true },
        { id: '4', type: 'phone', label: 'Phone Number', required: false },
        { id: '5', type: 'select', label: 'Company Size', required: true, options: ['1-10', '11-50', '51-200', '201-1000', '1000+'] },
        { id: '6', type: 'textarea', label: 'Tell us about your needs', required: false }
      ],
      submissions: 127,
      conversionRate: 23.5,
      status: 'active',
      createdBy: 'Lisa Chen',
      createdDate: '2024-01-15',
      lastModified: '2024-02-20'
    },
    {
      id: '2',
      name: 'Webinar Registration',
      type: 'event-registration',
      description: 'Registration form for monthly product webinars',
      fields: [
        { id: '1', type: 'text', label: 'First Name', required: true },
        { id: '2', type: 'text', label: 'Last Name', required: true },
        { id: '3', type: 'email', label: 'Email Address', required: true },
        { id: '4', type: 'text', label: 'Job Title', required: true },
        { id: '5', type: 'text', label: 'Company', required: true },
        { id: '6', type: 'select', label: 'Industry', required: false, options: ['Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Other'] }
      ],
      submissions: 89,
      conversionRate: 67.2,
      status: 'active',
      createdBy: 'David Rodriguez',
      createdDate: '2024-01-10',
      lastModified: '2024-02-15'
    },
    {
      id: '3',
      name: 'Newsletter Signup',
      type: 'newsletter',
      description: 'Simple email capture for newsletter subscriptions',
      fields: [
        { id: '1', type: 'text', label: 'First Name', required: true },
        { id: '2', type: 'email', label: 'Email Address', required: true },
        { id: '3', type: 'checkbox', label: 'I agree to receive marketing emails', required: true }
      ],
      submissions: 342,
      conversionRate: 45.8,
      status: 'active',
      createdBy: 'Emma Thompson',
      createdDate: '2024-01-05',
      lastModified: '2024-02-10'
    },
    {
      id: '4',
      name: 'Customer Satisfaction Survey',
      type: 'survey',
      description: 'Post-purchase customer feedback collection',
      fields: [
        { id: '1', type: 'text', label: 'Order Number', required: true },
        { id: '2', type: 'select', label: 'Overall Satisfaction', required: true, options: ['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very Dissatisfied'] },
        { id: '3', type: 'textarea', label: 'Comments and Suggestions', required: false },
        { id: '4', type: 'checkbox', label: 'May we contact you about this feedback?', required: false }
      ],
      submissions: 156,
      conversionRate: 31.2,
      status: 'active',
      createdBy: 'Lisa Chen',
      createdDate: '2024-01-20',
      lastModified: '2024-02-25'
    },
    {
      id: '5',
      name: 'Contact Us Form',
      type: 'contact',
      description: 'General inquiry form for website visitors',
      fields: [
        { id: '1', type: 'text', label: 'Name', required: true },
        { id: '2', type: 'email', label: 'Email', required: true },
        { id: '3', type: 'text', label: 'Subject', required: true },
        { id: '4', type: 'textarea', label: 'Message', required: true }
      ],
      submissions: 78,
      conversionRate: 19.4,
      status: 'draft',
      createdBy: 'David Rodriguez',
      createdDate: '2024-02-01',
      lastModified: '2024-02-28'
    }
  ]);

  const getFormTypeIcon = (type: string) => {
    switch (type) {
      case 'demo-request': return <Eye className="w-4 h-4" />;
      case 'event-registration': return <Calendar className="w-4 h-4" />;
      case 'newsletter': return <Mail className="w-4 h-4" />;
      case 'survey': return <BarChart3 className="w-4 h-4" />;
      case 'contact': return <Users className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getFormTypeColor = (type: string) => {
    switch (type) {
      case 'demo-request': return 'bg-blue-100 text-blue-800';
      case 'event-registration': return 'bg-green-100 text-green-800';
      case 'newsletter': return 'bg-purple-100 text-purple-800';
      case 'survey': return 'bg-orange-100 text-orange-800';
      case 'contact': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'paused': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCopyEmbedCode = (formId: string) => {
    const embedCode = `<iframe src="https://forms.theta.com/embed/${formId}" width="100%" height="500" frameborder="0"></iframe>`;
    navigator.clipboard.writeText(embedCode);
    alert('Embed code copied to clipboard!');
  };

  const handleDownloadSubmissions = (formName: string) => {
    // Simulate CSV download
    const csvContent = `Name,Email,Company,Submission Date\nJohn Doe,john@example.com,Acme Corp,2024-03-01\nJane Smith,jane@example.com,Tech Inc,2024-03-02`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formName.toLowerCase().replace(/\s+/g, '-')}-submissions.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const FormPreview: React.FC<{ form: MarketingForm }> = ({ form }) => (
    <div className="bg-white p-6 rounded-lg border max-w-md mx-auto">
      <h3 className="text-xl font-semibold mb-4">{form.name}</h3>
      <p className="text-gray-600 mb-6">{form.description}</p>
      
      <form className="space-y-4">
        {form.fields.map((field) => (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            
            {field.type === 'textarea' ? (
              <textarea
                placeholder={field.placeholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            ) : field.type === 'select' ? (
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select an option</option>
                {field.options?.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : field.type === 'checkbox' ? (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{field.label}</span>
              </div>
            ) : (
              <input
                type={field.type}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
          </div>
        ))}
        
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
        >
          Submit
        </button>
      </form>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Forms</h1>
          <p className="text-gray-600">Create and manage lead generation forms</p>
        </div>
        <div className="flex gap-2">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Form
          </button>
          <Link 
            to="/marketing/forms/complete"
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Professional Service Request
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('forms')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'forms'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Forms ({forms.length})
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'create'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Create New
          </button>
          <button
            onClick={() => setActiveTab('submissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'submissions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Submissions
          </button>
        </nav>
      </div>

      {/* Forms List */}
      {activeTab === 'forms' && (
        <div className="space-y-4">
          {forms.map((form) => (
            <div key={form.id} className="bg-white rounded-lg border p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${getFormTypeColor(form.type)}`}>
                    {getFormTypeIcon(form.type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{form.name}</h3>
                    <p className="text-gray-600 text-sm">{form.description}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(form.status)}`}>
                        {form.status.charAt(0).toUpperCase() + form.status.slice(1)}
                      </span>
                      <span className="text-xs text-gray-500">
                        Created by {form.createdBy} • {form.createdDate}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedForm(form);
                      setShowPreview(true);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Preview Form"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleCopyEmbedCode(form.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Copy Embed Code"
                  >
                    <Code className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDownloadSubmissions(form.name)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Download Submissions"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Form Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{form.submissions}</div>
                  <div className="text-sm text-gray-500">Submissions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{form.conversionRate}%</div>
                  <div className="text-sm text-gray-500">Conversion Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{form.fields.length}</div>
                  <div className="text-sm text-gray-500">Fields</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Form Tab */}
      {activeTab === 'create' && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold mb-4">Create New Form</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { type: 'demo-request', title: 'Demo Request', desc: 'Capture leads for product demos', icon: <Eye className="w-6 h-6" /> },
              { type: 'event-registration', title: 'Event Registration', desc: 'Webinar and event signups', icon: <Calendar className="w-6 h-6" /> },
              { type: 'newsletter', title: 'Newsletter Signup', desc: 'Email list building', icon: <Mail className="w-6 h-6" /> },
              { type: 'survey', title: 'Survey Form', desc: 'Customer feedback collection', icon: <BarChart3 className="w-6 h-6" /> },
              { type: 'contact', title: 'Contact Form', desc: 'General inquiries', icon: <Users className="w-6 h-6" /> },
              { type: 'lead-generation', title: 'Lead Generation', desc: 'Custom lead capture', icon: <Megaphone className="w-6 h-6" /> }
            ].map((template) => (
              <div key={template.type} className="border rounded-lg p-4 hover:border-blue-500 cursor-pointer transition-colors">
                <div className={`p-3 rounded-lg ${getFormTypeColor(template.type)} w-fit mb-3`}>
                  {template.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{template.title}</h3>
                <p className="text-sm text-gray-600">{template.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="bg-white rounded-lg border">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold">Recent Submissions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Form</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[
                  { form: 'Product Demo Request', name: 'John Doe', email: 'john@acme.com', date: '2024-03-01', status: 'new' },
                  { form: 'Webinar Registration', name: 'Jane Smith', email: 'jane@tech.com', date: '2024-03-01', status: 'contacted' },
                  { form: 'Newsletter Signup', name: 'Bob Johnson', email: 'bob@startup.io', date: '2024-02-29', status: 'subscribed' },
                  { form: 'Customer Survey', name: 'Alice Brown', email: 'alice@corp.com', date: '2024-02-29', status: 'completed' }
                ].map((submission, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{submission.form}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{submission.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{submission.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{submission.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        submission.status === 'new' ? 'bg-blue-100 text-blue-800' :
                        submission.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                        submission.status === 'subscribed' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Preview Modal */}
      {showPreview && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Form Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <FormPreview form={selectedForm} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingForms;
